import { useCallback, useState } from 'react';
import { Button, Col, Modal, ModalProps, Row } from 'react-bootstrap';
import { useMutation, useQuery } from '@apollo/client';
import {
  CloneEmojisFontFromProdMutation,
  CloneEmojisFontFromProdMutationVariables,
  EmojiFontInspectDataQuery,
  EmojiFontInspectDataQueryVariables,
} from '../../../../../../__gqltypes__';
import { CLONE_EMOJIS_FONT_FROM_PROD, EMOJI_FONT_INSPECT_DATA } from '../../../components/apolloQueries';
import { LoadingLogo } from '../../../../devtools/components/Modal';
import { ClipboardButton } from '../../../../curation/screens/SongSearch/components/ClipboardButton';

type Props = ModalProps;
type FontData = EmojiFontInspectDataQuery['app']['emojiFontInspectData'][0];

// Beta only. This gate is cosmetic — the authoritative check is server-side in
// `cloneEmojisFontFromProd`, which refuses to run unless the server namespace is Beta.
const CAN_SYNC_FONT = process.env.REACT_APP_NAMESPACE === 'beta';

export default function EmojiFontDataModal({show, onHide}: Props): JSX.Element {

  const { data, loading, refetch } = useQuery<
    EmojiFontInspectDataQuery,
    EmojiFontInspectDataQueryVariables
  >(EMOJI_FONT_INSPECT_DATA, {fetchPolicy: 'no-cache', skip: !show});

  const [cloneFromProd, { loading: syncing }] = useMutation<
    CloneEmojisFontFromProdMutation,
    CloneEmojisFontFromProdMutationVariables
  >(CLONE_EMOJIS_FONT_FROM_PROD);

  const [syncError, setSyncError] = useState<string | null>(null);
  const [syncResult, setSyncResult] = useState<string | null>(null);

  const handleCloneFromProd = useCallback((isOldAndroid: boolean) => {
    setSyncError(null);
    setSyncResult(null);
    cloneFromProd({ variables: { input: { isOldAndroid } } })
      .then((result) => {
        const res = result.data?.cloneEmojisFontFromProd;
        if (res) {
          setSyncResult(`Synced from prod — beta is now on version ${res.newVersion}, same as prod.`);
        }
        return refetch();
      })
      .catch((err) => setSyncError(err.message));
  }, [cloneFromProd, refetch]);

  const emojiFontData = data ? data.app.emojiFontInspectData : [];
  const renderData = (fontData: FontData, type: string, isOldAndroid: boolean) => {
    const updated = fontData ? new Date(fontData.updated) : null;
    return (
      <>
          <Row>
            <Col xs={3}>{type}</Col>
          </Row>
          <Row>
            <Col xs={3}>Version in cache</Col>
            <Col xs={6}>{fontData?.versionInCache}</Col>
          </Row>
          <Row>
            <Col xs={3}>Version</Col>
            <Col xs={6}>{fontData?.version}</Col>
          </Row>
          <Row>
            <Col xs={3}>Updated</Col>
            <Col xs={6}>{updated?.toString()}</Col>
          </Row>
          <Row>
            <Col xs={3}>Url</Col>
            <Col xs="auto">{fontData?.url}</Col>
          </Row>
          <Row>
            <Col xs={6}>
              <ClipboardButton title="Copy URL" textToCopy={fontData?.url ?? ''}/>
            </Col>
          </Row>
          {CAN_SYNC_FONT && (
            <Row className="mt-2">
              <Col xs={12}>
                <Button
                  type="button"
                  size="sm"
                  variant="warning"
                  disabled={syncing}
                  onClick={() => handleCloneFromProd(isOldAndroid)}
                >
                  <i className="fas fa-clone mr-2" />
                  Sync {type} font from Prod
                </Button>
              </Col>
            </Row>
          )}
        </>
    );
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Body>
        {loading ? (
          <LoadingLogo show={loading} />
        ) : (
        <>
          {CAN_SYNC_FONT && (
            <Row className="mb-3">
              <Col>
                <small className="text-muted">
                  Beta only. &quot;Sync from Prod&quot; copies Prod&apos;s <strong>latest</strong> font into Beta
                  under the <strong>same version number as Prod</strong>, so both environments stay directly
                  comparable. Prod is read-only and never modified.
                </small>
              </Col>
            </Row>
          )}
          {renderData(emojiFontData[0], 'Regular', false)}
          <div
            style={{
              width: '90%',
              height: '2px',
              backgroundColor: 'black',
              margin: '20px auto',
            }}
          />
          {renderData(emojiFontData[1], 'Old Android', true)}
          {syncResult && (
            <Row className="mt-3">
              <Col>
                <small className="text-success">{syncResult}</small>
              </Col>
            </Row>
          )}
          {syncError && (
            <Row className="mt-3">
              <Col>
                <small className="text-danger">Sync failed: {syncError}</small>
              </Col>
            </Row>
          )}
        </>
        )}
      </Modal.Body>
    </Modal>
  );
}